import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Divider,
  Checkbox,
  FormGroup,
  Slider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  LinearProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  Phone as PhoneIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import api from '../../services/api';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TIMEZONES = [
  'Europe/Dublin',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'Europe/Paris',
  'Europe/Berlin',
  'Australia/Sydney',
  'Asia/Tokyo'
];

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

/**
 * TimesOfServiceManager Component
 *
 * Admin panel for configuring Times of Service settings at platform level.
 */
const TimesOfServiceManager = () => {
  const [tabValue, setTabValue] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '', isRecurring: false });
  const [newContact, setNewContact] = useState({ name: '', phone: '', description: '' });

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/times-of-service');
      if (response.data.success) {
        setConfig(response.data.config);
      } else {
        setError('Failed to load configuration');
      }
    } catch (err) {
      console.error('Error fetching ToS config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Save configuration
  const saveConfig = async () => {
    try {
      setSaving(true);
      const response = await api.put('/admin/times-of-service', config);
      if (response.data.success) {
        setSuccess('Configuration saved successfully');
        setConfig(response.data.config);
      } else {
        setError('Failed to save configuration');
      }
    } catch (err) {
      console.error('Error saving ToS config:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Update config helper
  const updateConfig = (path, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      const parts = path.split('.');
      let current = newConfig;
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = { ...current[parts[i]] };
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return newConfig;
    });
  };

  // Schedule helpers
  const updateSchedule = (dayIndex, field, value) => {
    const newSchedule = [...(config.weeklySchedule || [])];
    if (!newSchedule[dayIndex]) {
      newSchedule[dayIndex] = { dayOfWeek: dayIndex, isOpen: false, periods: [] };
    }
    if (field === 'isOpen') {
      newSchedule[dayIndex] = { ...newSchedule[dayIndex], isOpen: value };
      if (value && (!newSchedule[dayIndex].periods || newSchedule[dayIndex].periods.length === 0)) {
        newSchedule[dayIndex].periods = [{ startTime: '09:00', endTime: '17:00' }];
      }
    } else if (field === 'startTime' || field === 'endTime') {
      newSchedule[dayIndex] = {
        ...newSchedule[dayIndex],
        periods: [{ ...newSchedule[dayIndex].periods?.[0], [field]: value }]
      };
    }
    updateConfig('weeklySchedule', newSchedule);
  };

  // Quick schedule presets
  const applySchedulePreset = (preset) => {
    let newSchedule;
    switch (preset) {
      case '24/7':
        newSchedule = DAYS_OF_WEEK.map((_, index) => ({
          dayOfWeek: index,
          isOpen: true,
          periods: [{ startTime: '00:00', endTime: '23:59' }]
        }));
        break;
      case 'business':
        newSchedule = DAYS_OF_WEEK.map((_, index) => ({
          dayOfWeek: index,
          isOpen: index >= 1 && index <= 5, // Mon-Fri
          periods: index >= 1 && index <= 5 ? [{ startTime: '09:00', endTime: '17:00' }] : []
        }));
        break;
      case 'extended':
        newSchedule = DAYS_OF_WEEK.map((_, index) => ({
          dayOfWeek: index,
          isOpen: index >= 1 && index <= 5, // Mon-Fri
          periods: index >= 1 && index <= 5 ? [{ startTime: '08:00', endTime: '20:00' }] : []
        }));
        break;
      case 'weekdays-weekends':
        newSchedule = DAYS_OF_WEEK.map((_, index) => ({
          dayOfWeek: index,
          isOpen: true,
          periods: [{
            startTime: (index === 0 || index === 6) ? '10:00' : '09:00',
            endTime: (index === 0 || index === 6) ? '16:00' : '17:00'
          }]
        }));
        break;
      default:
        return;
    }
    updateConfig('weeklySchedule', newSchedule);
  };

  // Holiday helpers
  const addHoliday = async () => {
    try {
      const response = await api.post('/admin/times-of-service/holiday', newHoliday);
      if (response.data.success) {
        fetchConfig();
        setHolidayDialogOpen(false);
        setNewHoliday({ name: '', date: '', isRecurring: false });
        setSuccess('Holiday added successfully');
      }
    } catch (err) {
      setError('Failed to add holiday');
    }
  };

  const deleteHoliday = async (holidayId) => {
    try {
      await api.delete(`/admin/times-of-service/holiday/${holidayId}`);
      fetchConfig();
      setSuccess('Holiday removed successfully');
    } catch (err) {
      setError('Failed to remove holiday');
    }
  };

  // Contact helpers
  const addContact = async () => {
    try {
      const response = await api.post('/admin/times-of-service/emergency-contact', newContact);
      if (response.data.success) {
        fetchConfig();
        setContactDialogOpen(false);
        setNewContact({ name: '', phone: '', description: '' });
        setSuccess('Contact added successfully');
      }
    } catch (err) {
      setError('Failed to add contact');
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await api.delete(`/admin/times-of-service/emergency-contact/${contactId}`);
      fetchConfig();
      setSuccess('Contact removed successfully');
    } catch (err) {
      setError('Failed to remove contact');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading configuration...</Typography>
      </Box>
    );
  }

  if (!config) {
    return (
      <Alert severity="error">Failed to load Times of Service configuration</Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Times of Service Configuration</Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={saveConfig}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Master Toggle */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={config.enabled || false}
              onChange={(e) => updateConfig('enabled', e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="subtitle1">Enable Times of Service</Typography>
              <Typography variant="body2" color="text.secondary">
                When disabled, AI Therapist is available 24/7
              </Typography>
            </Box>
          }
        />
      </Paper>

      {config.enabled && (
        <>
          <Paper sx={{ mb: 3 }}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab icon={<ScheduleIcon />} label="Schedule" />
              <Tab icon={<EventIcon />} label="Holidays" />
              <Tab icon={<SettingsIcon />} label="Grace Period" />
              <Tab icon={<SecurityIcon />} label="Out-of-Hours" />
              <Tab icon={<PhoneIcon />} label="Emergency" />
            </Tabs>

            {/* Schedule Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <FormLabel>Display Timezone</FormLabel>
                  <Select
                    value={config.defaultTimezone || 'Europe/Dublin'}
                    onChange={(e) => updateConfig('defaultTimezone', e.target.value)}
                    size="small"
                    sx={{ maxWidth: 300 }}
                  >
                    {TIMEZONES.map(tz => (
                      <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Quick Presets */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Quick Presets</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applySchedulePreset('24/7')}
                  >
                    24/7 Access
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applySchedulePreset('business')}
                  >
                    Business Hours (Mon-Fri 9-5)
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applySchedulePreset('extended')}
                  >
                    Extended Hours (Mon-Fri 8-8)
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => applySchedulePreset('weekdays-weekends')}
                  >
                    7 Days (Shorter Weekends)
                  </Button>
                </Box>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 2 }}>Weekly Schedule (UTC)</Typography>

              {DAYS_OF_WEEK.map((day, index) => {
                const daySchedule = config.weeklySchedule?.find(s => s.dayOfWeek === index) || { isOpen: false, periods: [] };
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={daySchedule.isOpen}
                          onChange={(e) => updateSchedule(index, 'isOpen', e.target.checked)}
                        />
                      }
                      label={<Typography sx={{ width: 100 }}>{day}</Typography>}
                    />
                    {daySchedule.isOpen ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          type="time"
                          size="small"
                          value={daySchedule.periods?.[0]?.startTime || '09:00'}
                          onChange={(e) => updateSchedule(index, 'startTime', e.target.value)}
                          sx={{ width: 120 }}
                        />
                        <Typography>to</Typography>
                        <TextField
                          type="time"
                          size="small"
                          value={daySchedule.periods?.[0]?.endTime || '17:00'}
                          onChange={(e) => updateSchedule(index, 'endTime', e.target.value)}
                          sx={{ width: 120 }}
                        />
                      </Box>
                    ) : (
                      <Typography color="text.secondary">Closed</Typography>
                    )}
                  </Box>
                );
              })}

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ mb: 2 }}>Delegation Settings</Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.allowServiceGroupOverride !== false}
                      onChange={(e) => updateConfig('allowServiceGroupOverride', e.target.checked)}
                    />
                  }
                  label="Allow Service Groups to override platform schedule"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.allowClinicalSupervisorEdit !== false}
                      onChange={(e) => updateConfig('allowClinicalSupervisorEdit', e.target.checked)}
                    />
                  }
                  label="Allow Clinical Supervisors to edit group schedules"
                />
              </FormGroup>
            </TabPanel>

            {/* Holidays Tab */}
            <TabPanel value={tabValue} index={1}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setHolidayDialogOpen(true)}
                sx={{ mb: 2 }}
              >
                Add Holiday
              </Button>

              <List>
                {config.holidayClosures?.map((holiday, index) => (
                  <ListItem key={holiday._id || index}>
                    <ListItemText
                      primary={holiday.name}
                      secondary={
                        holiday.isRecurring
                          ? `Recurring annually`
                          : new Date(holiday.date).toLocaleDateString()
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => deleteHoliday(holiday._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {(!config.holidayClosures || config.holidayClosures.length === 0) && (
                  <Typography color="text.secondary">No holidays configured</Typography>
                )}
              </List>
            </TabPanel>

            {/* Grace Period Tab */}
            <TabPanel value={tabValue} index={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.gracePeriod?.enabled !== false}
                    onChange={(e) => updateConfig('gracePeriod.enabled', e.target.checked)}
                  />
                }
                label="Enable Grace Period"
              />

              {config.gracePeriod?.enabled !== false && (
                <Box sx={{ mt: 3 }}>
                  <Typography gutterBottom>
                    Warning before close: {config.gracePeriod?.warningMinutes || 15} minutes
                  </Typography>
                  <Slider
                    value={config.gracePeriod?.warningMinutes || 15}
                    onChange={(e, v) => updateConfig('gracePeriod.warningMinutes', v)}
                    min={5}
                    max={60}
                    step={5}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ maxWidth: 400 }}
                  />

                  <Typography gutterBottom sx={{ mt: 3 }}>
                    Soft close duration: {config.gracePeriod?.softCloseMinutes || 5} minutes
                  </Typography>
                  <Slider
                    value={config.gracePeriod?.softCloseMinutes || 5}
                    onChange={(e, v) => updateConfig('gracePeriod.softCloseMinutes', v)}
                    min={1}
                    max={30}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ maxWidth: 400 }}
                  />
                </Box>
              )}
            </TabPanel>

            {/* Out-of-Hours Tab */}
            <TabPanel value={tabValue} index={3}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Out-of-Hours Experience</FormLabel>
                <RadioGroup
                  value={config.outOfHoursExperience?.mode || 'helper_persona'}
                  onChange={(e) => updateConfig('outOfHoursExperience.mode', e.target.value)}
                >
                  <FormControlLabel
                    value="helper_persona"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography>Helper Persona (Recommended)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Limited AI support for journaling and check-ins
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="closed_screen"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography>Closed Screen</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Simple message with next availability
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="limited_tools"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography>Limited Tools Only</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Access to journal and check-in without chat
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {config.outOfHoursExperience?.mode === 'closed_screen' && (
                <TextField
                  label="Closed Screen Message"
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ mt: 3 }}
                  value={config.outOfHoursExperience?.closedScreenMessage || ''}
                  onChange={(e) => updateConfig('outOfHoursExperience.closedScreenMessage', e.target.value)}
                />
              )}

              <Divider sx={{ my: 3 }} />

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.outOfHoursExperience?.allowJournalAccess !== false}
                      onChange={(e) => updateConfig('outOfHoursExperience.allowJournalAccess', e.target.checked)}
                    />
                  }
                  label="Allow Journal Access"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.outOfHoursExperience?.allowCheckInAccess !== false}
                      onChange={(e) => updateConfig('outOfHoursExperience.allowCheckInAccess', e.target.checked)}
                    />
                  }
                  label="Allow Check-In Access"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.outOfHoursExperience?.showEmergencyContactButton !== false}
                      onChange={(e) => updateConfig('outOfHoursExperience.showEmergencyContactButton', e.target.checked)}
                    />
                  }
                  label="Show Emergency Contact Button"
                />
              </FormGroup>
            </TabPanel>

            {/* Emergency Override Tab */}
            <TabPanel value={tabValue} index={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.emergencyOverride?.enabled !== false}
                    onChange={(e) => updateConfig('emergencyOverride.enabled', e.target.checked)}
                  />
                }
                label="Enable Emergency Access Override"
              />

              {config.emergencyOverride?.enabled !== false && (
                <Box sx={{ mt: 3 }}>
                  <Typography gutterBottom>
                    Maximum duration: {config.emergencyOverride?.maxDurationHours || 4} hours
                  </Typography>
                  <Slider
                    value={config.emergencyOverride?.maxDurationHours || 4}
                    onChange={(e, v) => updateConfig('emergencyOverride.maxDurationHours', v)}
                    min={1}
                    max={24}
                    step={1}
                    marks={[{ value: 4, label: '4h' }, { value: 12, label: '12h' }, { value: 24, label: '24h' }]}
                    valueLabelDisplay="auto"
                    sx={{ maxWidth: 400 }}
                  />

                  <FormGroup sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.emergencyOverride?.requireReason !== false}
                          onChange={(e) => updateConfig('emergencyOverride.requireReason', e.target.checked)}
                        />
                      }
                      label="Require reason for granting access"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.emergencyOverride?.notifyAdmin !== false}
                          onChange={(e) => updateConfig('emergencyOverride.notifyAdmin', e.target.checked)}
                        />
                      }
                      label="Notify admin when access is granted"
                    />
                  </FormGroup>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ mb: 2 }}>Emergency Contacts</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setContactDialogOpen(true)}
                sx={{ mb: 2 }}
              >
                Add Emergency Contact
              </Button>

              <List>
                {config.outOfHoursExperience?.emergencyContacts?.map((contact, index) => (
                  <ListItem key={contact._id || index}>
                    <ListItemText
                      primary={contact.name}
                      secondary={`${contact.phone}${contact.description ? ` - ${contact.description}` : ''}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => deleteContact(contact._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {(!config.outOfHoursExperience?.emergencyContacts || config.outOfHoursExperience.emergencyContacts.length === 0) && (
                  <Typography color="text.secondary">No emergency contacts configured</Typography>
                )}
              </List>
            </TabPanel>
          </Paper>
        </>
      )}

      {/* Add Holiday Dialog */}
      <Dialog open={holidayDialogOpen} onClose={() => setHolidayDialogOpen(false)}>
        <DialogTitle>Add Holiday Closure</DialogTitle>
        <DialogContent>
          <TextField
            label="Holiday Name"
            fullWidth
            margin="normal"
            value={newHoliday.name}
            onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newHoliday.isRecurring}
                onChange={(e) => setNewHoliday({ ...newHoliday, isRecurring: e.target.checked })}
              />
            }
            label="Recurring Annually"
          />
          {newHoliday.isRecurring ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Select
                  fullWidth
                  value={newHoliday.recurringMonth || 0}
                  onChange={(e) => setNewHoliday({ ...newHoliday, recurringMonth: e.target.value })}
                >
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                    <MenuItem key={i} value={i}>{m}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="number"
                  fullWidth
                  label="Day"
                  inputProps={{ min: 1, max: 31 }}
                  value={newHoliday.recurringDay || 1}
                  onChange={(e) => setNewHoliday({ ...newHoliday, recurringDay: parseInt(e.target.value) })}
                />
              </Grid>
            </Grid>
          ) : (
            <TextField
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              label="Date"
              value={newHoliday.date}
              onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHolidayDialogOpen(false)}>Cancel</Button>
          <Button onClick={addHoliday} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)}>
        <DialogTitle>Add Emergency Contact</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newContact.name}
            onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
          />
          <TextField
            label="Phone Number"
            fullWidth
            margin="normal"
            value={newContact.phone}
            onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={newContact.description}
            onChange={(e) => setNewContact({ ...newContact, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button onClick={addContact} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimesOfServiceManager;
